if (!customElements.get('article-toc')) {
  customElements.define('article-toc', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = this.closest('.mbx-article');
      this.content = this.root?.querySelector('.mbx-article__content');
      if (!this.content) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.desktop = window.matchMedia('(min-width: 991px)');
      this.details = this.querySelector('details');
      this.list = this.querySelector('nav > ol');
      this.progress = this.querySelector('[role="progressbar"]');
      this.querySelector('.mbx-toc__top').addEventListener('click', () => {
        history.replaceState(null, '', location.pathname + location.search);
        const title = this.root.querySelector('h1');
        if (title) {
          const hadTabindex = title.hasAttribute('tabindex');
          if (!hadTabindex) title.setAttribute('tabindex', '-1');
          title.focus({ preventScroll: true });
          if (!hadTabindex) title.addEventListener('blur', () => title.removeAttribute('tabindex'), { once: true });
        }
        window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      this.mobileSlot = this.root.querySelector('[data-article-toc-mobile]');
      this.desktopSlot = this.root.querySelector('[data-article-toc-desktop]');
      this.desktop.addEventListener('change', () => this.place(), options);
      this.addEventListener('click', (event) => this.navigate(event), options);
      this.details.addEventListener('toggle', () => {
        if (this.desktop.matches) this.details.open = true;
      }, options);
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.highlight(); });
      };
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      window.addEventListener('hashchange', () => this.restoreAnchor(), options);
      this.observer = new MutationObserver(() => {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => this.build(), 100);
      });
      this.observer.observe(this.content, { childList: true, subtree: true, characterData: true });
      this.build();
      this.place();
      if (document.readyState === 'complete') this.restoreAnchor();
      else window.addEventListener('load', () => this.restoreAnchor(), { ...options, once: true });
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.controller?.abort();
        this.controller = null;
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        clearTimeout(this.refreshTimer);
      });
    }

    place() {
      const slot = this.desktop.matches && this.desktopSlot ? this.desktopSlot : this.mobileSlot;
      if (this.parentElement !== slot) slot.append(this);
      this.details.open = this.desktop.matches;
      if (this.desktop.matches) this.querySelector('summary').setAttribute('tabindex', '-1');
      else this.querySelector('summary').removeAttribute('tabindex');
      this.highlight();
    }

    build() {
      const excluded = '[data-toc-exclude], [data-shortcode-product], .article-product-card, .mbx-article-products, .product-card, .card--product, .mbx-recipe__related, nav, aside, [hidden], [aria-hidden="true"]';
      this.headings = Array.from(this.content.querySelectorAll(this.dataset.levels === 'h2-h3' ? 'h2, h3' : 'h2'))
        .filter((heading) => heading.textContent.trim() && !heading.closest(excluded) && heading.getClientRects().length);
      this.list.replaceChildren();
      this.hidden = this.headings.length < 2;
      this.root.classList.toggle('mbx-article--has-toc', !this.hidden);
      if (this.hidden) return;
      let parentItem;
      let childList;
      this.links = this.headings.map((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const base = `${this.dataset.prefix}-${index + 1}`;
          let id = base;
          let suffix = 1;
          while (document.getElementById(id)) id = `${base}-${suffix++}`;
          heading.id = id;
        }
        heading.classList.add('mbx-toc-target');
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.setAttribute('href', `#${encodeURIComponent(heading.id)}`);
        link.textContent = heading.textContent.trim();
        item.append(link);
        if (heading.tagName === 'H3' && parentItem) {
          if (!childList) { childList = document.createElement('ol'); parentItem.append(childList); }
          childList.append(item);
        } else {
          this.list.append(item);
          parentItem = heading.tagName === 'H2' ? item : null;
          childList = null;
        }
        return link;
      });
      this.querySelector('.mbx-toc__count').textContent = `${this.list.children.length} sections`;
      this.highlight();
    }

    offset() {
      let bottom = 0;
      document.querySelectorAll('.cc-header--sticky').forEach((header) => {
        const rect = header.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) bottom = Math.max(bottom, rect.bottom);
      });
      const offset = Math.max(24, bottom + 20);
      this.root.style.setProperty('--mbx-toc-offset', `${offset}px`);
      return offset;
    }

    restoreAnchor() {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const heading = this.headings.find((item) => item.id === id);
      if (!heading) return;
      this.offset();
      heading.scrollIntoView({ behavior: 'instant', block: 'start' });
      this.highlight();
    }

    highlight() {
      if (this.hidden || !this.links?.length) return;
      const offset = this.offset();
      const bounds = this.content.getBoundingClientRect();
      const distance = Math.max(1, bounds.height - (window.innerHeight - offset));
      const progress = Math.max(0, Math.min(1, (offset - bounds.top) / distance));
      this.style.setProperty('--toc-progress', progress);
      this.progress.setAttribute('aria-valuenow', Math.round(progress * 100));
      let current = 0;
      this.headings.forEach((heading, index) => {
        if (heading.getBoundingClientRect().top <= offset + 8) current = index;
      });
      this.links.forEach((link, index) => {
        if (index === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    navigate(event) {
      const link = event.target.closest('a');
      const index = this.links?.indexOf(link) ?? -1;
      if (index < 0 || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const heading = this.headings[index];
      this.offset();
      history.pushState(null, '', link.getAttribute('href'));
      const hadTabindex = heading.hasAttribute('tabindex');
      if (!hadTabindex) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      if (!hadTabindex) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
      heading.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }
  });
}
